import { Grid } from "@mantine/core";
import { useParams } from "react-router-dom";
import AgreementForm from "./AgreementForm";
import ActionCard from "src/components/ActionCard";
import { useGetAgreementLazyQuery } from "src/generated/graphql";
import { useCallback, useEffect } from "react";
import RecurringChargeList from "../RecurringCharge/RecurringChargeList";
import InitialChargeList from "src/modules/InitialCharge/InitialChargeList";
import ServiceAllotmentList from "src/modules/ServiceAllotment/ServiceAllotmentList";
import AccessPeriodDetail from "./AccessPeriodDetail";
import { useModals } from "@mantine/modals";
import useWritePermission from "src/hooks/usePermission";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

type Props = {
  isOrg?: boolean;
};

export default function AgreementDetail({ isOrg }: Props) {
  const { agreementId } = useParams<{ agreementId: string }>();
  const modals = useModals();
  const [getAgreement, { data: agreementData }] = useGetAgreementLazyQuery();
  const isAllowWrite = useWritePermission(
    isOrg ? EMPLOYEE_ROLE_OBJ.orgAgreements : EMPLOYEE_ROLE_OBJ.locAgreements
  );

  useEffect(() => {
    agreementId &&
      getAgreement({
        variables: { id: agreementId },
      });
  }, [agreementId]);

  const handleModal = useCallback(
    (allotment = null) => {
      agreementId &&
        modals.openModal({
          closeOnClickOutside: false,
          title: `${allotment ? "Edit" : "Create"} Access Period`,
          children: (
            <AccessPeriodDetail
              formData={allotment}
              isModal={true}
              agreementId={agreementId}
              isOrg={isOrg}
              isAllowWrite={isAllowWrite}
            />
          ),
        });
    },
    [agreementId, isAllowWrite]
  );

  return (
    <Grid sx={{ width: "100%", justifyContent: "center" }} gutterMd={"xl"}>
      <Grid.Col span={12} md={6}>
        <AgreementForm
          agreementData={agreementData?.agreementOne}
          isOrg={isOrg}
          isAllowWrite={isAllowWrite}
        />
      </Grid.Col>

      <Grid.Col span={12} md={6}>
        <ActionCard title="Charges">
          <InitialChargeList
            sx={{ minHeight: "15vh" }}
            title={"Initial"}
            initialCharges={agreementData?.agreementOne?.initialCharges}
            disabled={agreementData?.agreementOne?.isOrg && !isOrg}
            isAllowWrite={isAllowWrite}
            isEditable={
              agreementData?.agreementOne?.isOrg
                ? agreementData?.agreementOne?.isOrg && isOrg
                : true
            }
          />
          <RecurringChargeList
            sx={{ minHeight: "15vh" }}
            recurringCharges={
              agreementData?.agreementOne?.recurringCharges || []
            }
            disabled={agreementData?.agreementOne?.isOrg && !isOrg}
            isAllowWrite={isAllowWrite}
          />
        </ActionCard>

        <ActionCard title="Allotments" mt={"md"}>
          <ServiceAllotmentList
            sx={{ minHeight: "15vh" }}
            title={"Initial"}
            Allotments={agreementData?.agreementOne?.initialAllotments}
            disabled={agreementData?.agreementOne?.isOrg && !isOrg}
            isAllowWrite={isAllowWrite}
            isEditable={
              agreementData?.agreementOne?.isOrg
                ? agreementData?.agreementOne?.isOrg && isOrg
                : true
            }
          />
          <ServiceAllotmentList
            sx={{ minHeight: "15vh" }}
            title={"Recurring"}
            Allotments={agreementData?.agreementOne?.recurringAllotments}
            isRecurring={true}
            disabled={agreementData?.agreementOne?.isOrg && !isOrg}
            isAllowWrite={isAllowWrite}
            isEditable={
              agreementData?.agreementOne?.isOrg
                ? agreementData?.agreementOne?.isOrg && isOrg
                : true
            }
          />
        </ActionCard>

        {agreementData?.agreementOne?.isMembership && (
          <ActionCard
            title="General Access Periods"
            mt={"md"}
            handleModal={
              isAllowWrite && isOrg === agreementData?.agreementOne?.isOrg
                ? () => handleModal()
                : undefined
            }
          >
            {agreementData?.agreementOne?.accessPeriods?.map((e) => (
              <AccessPeriodDetail
                key={e.id}
                formData={e}
                handleModal={isAllowWrite ? handleModal : undefined}
                agreementId={agreementId || ""}
                isOrg={agreementData?.agreementOne?.isOrg}
                isAllowWrite={isAllowWrite}
              />
            ))}
          </ActionCard>
        )}
      </Grid.Col>
    </Grid>
  );
}
